/**
 * Per-tab session storage for aniOS windows.
 *
 * Survives reload within the same tab but is cleared when the tab closes or on logout.
 * Durable workspace-config never stores open windows (avoids million-window restores on login).
 */

import type { WorkspaceConfigWindow } from "@/services/api/workspace-config/types";

const STORAGE_KEY_PREFIX = "ani.os-session-windows.";

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

interface CachedSessionWindows {
  ownerUserId: string;
  windows: WorkspaceConfigWindow[];
}

/**
 * Read windows persisted for the current browser tab session.
 *
 * @param userId - Authenticated user id
 * @returns Window list or null when missing/invalid
 */
export function readSessionWindows(userId: string): WorkspaceConfigWindow[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSessionWindows;
    if (parsed.ownerUserId !== userId) return null;
    return parsed.windows;
  } catch {
    return null;
  }
}

/**
 * Persist windows for the current browser tab session.
 *
 * @param userId - Authenticated user id
 * @param windows - Serializable window snapshot
 */
export function writeSessionWindows(
  userId: string,
  windows: WorkspaceConfigWindow[],
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedSessionWindows = { ownerUserId: userId, windows };
    sessionStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // ignore quota / serialization errors
  }
}

/**
 * Remove session-scoped windows for a user (e.g. on logout).
 *
 * @param userId - Authenticated user id
 */
export function clearSessionWindows(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}
